import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {RevaIndicationEndpoint} from "@reva/shared/spec-conversion";

import type {ValueIndicationRequest, ValueIndicationResponse} from "../api/payload-types/value-indication";
import type {QueryError} from "../util/errorUtil";
import {buildApiQueryError} from "../util/errorUtil";
import {combineIndicationData, combineValueData} from "../util/valueIndicationUtil";
import type {ApiQueryError} from "./types";

/**
 * One indication method's parameters split into request input, response output and their merge.
 * Internally aggregated for the slice from the response + request data — not raw API data.
 */
export interface IndicationParameterDetails {
    input: Record<string, unknown>;
    output: Record<string, unknown>;
    combined: Record<string, unknown>;
}

interface ValueIndicationState {
    loading: boolean;
    missingPermissions: boolean;
    requestData: ValueIndicationRequest | null;
    indicationData: ValueIndicationResponse | null;
    combinedIndicationData: Record<RevaIndicationEndpoint, IndicationParameterDetails | null> | null;
    queryError: ApiQueryError | null;
    editedValueDataGroup: Record<string, boolean> | null;
    expandedGroups: Record<string, Record<string, boolean>>;
}

const initialState: ValueIndicationState = {
    loading: false,
    missingPermissions: false,
    requestData: null,
    indicationData: null,
    combinedIndicationData: null,
    queryError: null,
    editedValueDataGroup: null,
    expandedGroups: {},
};

const valueIndicationSlice = createSlice({
    name: "valueIndication",
    initialState,
    reducers: {
        clearIndicationData: (state) => {
            state.loading = false;
            state.missingPermissions = false;
            state.indicationData = null;
            state.combinedIndicationData = null;
            state.requestData = null;
            state.queryError = null;
            state.expandedGroups = {};
        },
        clearLoading: (state) => {
            state.loading = false;
            state.missingPermissions = false;
            state.indicationData = null;
            state.combinedIndicationData = null;
            state.requestData = null;
        },
        setLoading: (state) => {
            state.loading = true;
            state.missingPermissions = false;
            state.requestData = null;
            state.queryError = null;
        },
        setMissingPermissions: (state) => {
            state.loading = false;
            state.missingPermissions = true;
            state.indicationData = null;
            state.combinedIndicationData = null;
            state.requestData = null;
            state.queryError = null;
        },
        setQueryError: (state, action: PayloadAction<QueryError>) => {
            state.loading = false;
            state.missingPermissions = false;
            state.indicationData = null;
            state.combinedIndicationData = null;
            state.requestData = null;
            state.queryError = buildApiQueryError(action.payload);
        },
        initEndpointExpandedGroups: (
            state,
            action: PayloadAction<{endpoint: RevaIndicationEndpoint; groups: Record<string, boolean>}>,
        ) => {
            const {endpoint, groups} = action.payload;
            const current = state.expandedGroups[endpoint];
            if (!current) {
                state.expandedGroups[endpoint] = groups;
                return;
            }
            // Mehrere Tabellen je Endpoint mit eigenen Gruppen (ENERGY_VALUE: Vorschau + Wert-Tab, #19096) — nur
            // unbekannte Gruppen ergänzen, sonst no-op, damit der init-Effekt keine Render-Schleife auslöst.
            for (const [groupKey, expanded] of Object.entries(groups)) {
                if (!(groupKey in current)) current[groupKey] = expanded;
            }
        },
        toggleExpandedGroup: (state, action: PayloadAction<{endpoint: RevaIndicationEndpoint; groupKey: string}>) => {
            const {endpoint, groupKey} = action.payload;
            const current = state.expandedGroups[endpoint] ?? {};
            current[groupKey] = current[groupKey] === false; // flip false -> true, true/undefined -> false
            state.expandedGroups[endpoint] = current;
        },
        setIndicationData: (
            state,
            action: PayloadAction<{requestData: ValueIndicationRequest; indicationData: ValueIndicationResponse}>,
        ) => {
            state.loading = false;
            state.missingPermissions = false;
            const {indicationData, requestData} = action.payload;
            state.indicationData = indicationData;
            state.requestData = requestData;
            state.combinedIndicationData = {
                VALUE: combineValueData(indicationData, requestData),
                INCOME_VALUE: combineIndicationData(indicationData, requestData, indicationData.income_value_details),
                REAL_ASSET_VALUE: combineIndicationData(
                    indicationData,
                    requestData,
                    indicationData.real_asset_value_details,
                ),
                COMPARATIVE_VALUE: combineIndicationData(
                    indicationData,
                    requestData,
                    indicationData.comparative_value_details,
                ),
                ENERGY_VALUE: combineIndicationData(indicationData, requestData, indicationData.energy_value_details),
            };
            state.queryError = null;
        },
        setEditedValueDataGroup: (state, action: PayloadAction<string | null>) => {
            state.editedValueDataGroup = action.payload === null ? null : {[action.payload]: true};
        },
    },
    selectors: {
        selectLoading: (state) => state.loading,
        selectMissingPermissions: (state) => state.missingPermissions,
        selectIndicationData: (state) => state.indicationData,
        selectRequestData: (state) => state.requestData,
        selectCombinedIndicationData: (state) => state.combinedIndicationData,
        selectValueData: (state) => state.combinedIndicationData?.VALUE,
        selectIncomeValueData: (state) => state.combinedIndicationData?.INCOME_VALUE,
        selectRealAssetValueData: (state) => state.combinedIndicationData?.REAL_ASSET_VALUE,
        selectComparativeValueData: (state) => state.combinedIndicationData?.COMPARATIVE_VALUE,
        selectEnergyValueData: (state) => state.combinedIndicationData?.ENERGY_VALUE,
        selectValueDataAvailable: (state) => !!state.combinedIndicationData?.VALUE,
        selectIncomeValueAvailable: (state) => !!state.combinedIndicationData?.INCOME_VALUE,
        selectRealAssetValueAvailable: (state) => !!state.combinedIndicationData?.REAL_ASSET_VALUE,
        selectComparativeValueAvailable: (state) => !!state.combinedIndicationData?.COMPARATIVE_VALUE,
        selectError: (state) => state.queryError,
        selectEditedValueDataGroup: (state) => state.editedValueDataGroup,
        selectExpandedGroupsByEndpoint: (state) => state.expandedGroups,
    },
});

export const {
    clearIndicationData,
    setLoading,
    clearLoading,
    setMissingPermissions,
    setQueryError,
    setIndicationData,
    setEditedValueDataGroup,
    initEndpointExpandedGroups,
    toggleExpandedGroup,
} = valueIndicationSlice.actions;

export const {
    selectLoading,
    selectMissingPermissions,
    selectValueData,
    selectCombinedIndicationData,
    selectIndicationData,
    selectRequestData,
    selectIncomeValueData,
    selectRealAssetValueData,
    selectComparativeValueData,
    selectEnergyValueData,
    selectValueDataAvailable,
    selectIncomeValueAvailable,
    selectRealAssetValueAvailable,
    selectComparativeValueAvailable,
    selectError,
    selectEditedValueDataGroup,
    selectExpandedGroupsByEndpoint,
} = valueIndicationSlice.selectors;

export default valueIndicationSlice.reducer;
